/**
 * Shared utilities and helpers for seat layout components
 */

export const columns = ['A', 'B', 'C', 'D']

/**
 * Helper function to get price for seat type and age group
 */
export const getSeatPrice = (seatType, ageGroup, tripData = null, priceDetails = []) => {
    // Use trip-specific pricing data if available, otherwise fall back to priceDetails
    const pricingData = tripData?.price || priceDetails
    
    if (!pricingData || !Array.isArray(pricingData)) {
        return 0
    }
    
    const seatPriceData = pricingData.find(price => price.seat_type_code === seatType)
    if (!seatPriceData) {
        return 0
    }
    
    // Check for new consolidated price structure first
    if (seatPriceData.user_preferred_consolidated_price) {
        const ageGroupMap = {
            'ADL': 'ADL',
            'CHD': 'CHD', 
            'INF': 'INF',
            'SNR': 'SNR'
        }
        const mappedAgeGroup = ageGroupMap[ageGroup] || 'ADL'
        return seatPriceData.user_preferred_consolidated_price[mappedAgeGroup] || 0
    }
    
    // Fallback to stop_price structure for backward compatibility
    if (seatPriceData.stop_price && Array.isArray(seatPriceData.stop_price)) {
        // Get the first stop price (assuming single segment trips)
        const stopPrice = seatPriceData.stop_price[0]
        if (!stopPrice || !stopPrice.price) {
            return 0
        }
        
        // Map age group codes
        const ageGroupMap = {
            'ADL': 'ADL',
            'CHD': 'CHD', 
            'INF': 'INF',
            'SNR': 'SNR'
        }
        
        const mappedAgeGroup = ageGroupMap[ageGroup] || 'ADL'
        return stopPrice.price[mappedAgeGroup] || 0
    }
    
    return 0
}

/**
 * Update departure seat counts
 */
export const updateDepartureSeatCounts = (seats, setDepartureSeatCounts) => {
    setDepartureSeatCounts({
        STD: seats.filter(seat => seat.seat_type === 'STD' && seat.seat_status !== 'BLK')?.length,
        VIP: seats.filter(seat => seat.seat_type === 'VIP' && seat.seat_status !== 'BLK')?.length,
    })
}

/**
 * Update return seat counts
 */
export const updateReturnSeatCounts = (seats, setReturnSeatCounts) => {
    setReturnSeatCounts({
        STD: seats.filter(seat => seat.seat_type === 'STD' && seat.seat_status !== 'BLK')?.length,
        VIP: seats.filter(seat => seat.seat_type === 'VIP' && seat.seat_status !== 'BLK')?.length,
    })
}

/**
 * Update seat counts
 */
export const updateSeatCounts = (seats, setSeatAvailableCount) => {
    setSeatAvailableCount({
        STD: seats.filter(seat => seat.seat_type === 'STD' && seat.seat_status !== 'BLK')?.length,
        VIP: seats.filter(seat => seat.seat_type === 'VIP' && seat.seat_status !== 'BLK')?.length,
    })
}

/**
 * Check if this is a VIP-only vessel based on layout key
 * @param {string} layoutKey - The seat_layout_key from API response
 * @returns {boolean} - True if VIP-only vessel layout
 */
export const isVIPOnlyVessel = (layoutKey) => {
    return layoutKey === 'VIP_seat_vessels'
}

/**
 * Prepare boat layout for standard vessels
 */
export const prepareStandardBoatLayout = (
    availableSeats,
    assignedSeats,
    currentLayoutKey
) => {
    // For all_std_seat_vessels, only include rows 1-5 (exclude 6 and 7)
    const seatRows = [
        currentLayoutKey === 'all_std_seat_vessels' 
            ? ['1', '2', '3', '4', '5'] 
            : ['1', '2', '3', '4', '5', '6', '7'], // Seat Numbers
    ]
    
    let seatLayout = []
    seatRows[0].forEach(row => {
        columns.forEach(column => {
            seatLayout.push({
                row: parseInt(row),
                column: column,
                seat_type: parseInt(row) <= 2 ? 'VIP' : 'STD', // First 2 rows VIP, remaining STD
                available: false, // Default availability
            })
        })
    })
    
    // Update seat layout with actual seat data from API
    availableSeats.forEach(seat => {
        const seatIndex = seatLayout.findIndex(s => `${s.row}${s.column}` === seat.seat_no)
        if (seatIndex !== -1) {
            // For all_std_seat_vessels layout, convert VIP seats to STD
            let seatType = seat.seat_type
            if (currentLayoutKey === 'all_std_seat_vessels' && seat.seat_type === 'VIP') {
                seatType = 'STD'
            }
            seatLayout[seatIndex].seat_type = seatType
            // Mark seat as unavailable if status is BLK (blocked), BKD (booked), BOOKED, or already assigned
            const isAssigned = assignedSeats.includes(seat.seat_no)
            const isBooked = seat.seat_status === 'BKD' || seat.seat_status === 'BOOKED'
            const isBlocked = seat.seat_status === 'BLK'
            // BKD/BOOKED seats should be marked as unavailable but still rendered
            seatLayout[seatIndex].available = !isBooked && !isBlocked && !isAssigned
        } else {
            // If seat doesn't exist in grid (shouldn't happen), add it
            const match = seat.seat_no.match(/^(\d+)([A-D])$/)
            if (match) {
                const row = parseInt(match[1])
                const column = match[2]
                let seatType = seat.seat_type
                if (currentLayoutKey === 'all_std_seat_vessels' && seat.seat_type === 'VIP') {
                    seatType = 'STD'
                }
                const isAssigned = assignedSeats.includes(seat.seat_no)
                const isBooked = seat.seat_status === 'BKD' || seat.seat_status === 'BOOKED'
                const isBlocked = seat.seat_status === 'BLK'
                
                seatLayout.push({
                    row: row,
                    column: column,
                    seat_type: seatType,
                    available: !isBooked && !isBlocked && !isAssigned
                })
            }
        }
    })

    // Split the seat layout into VIP and STD arrays
    // For all_std_seat_vessels, all seats should be STD
    const vipSeats = currentLayoutKey === 'all_std_seat_vessels' ? [] : seatLayout.filter(seat => seat.seat_type === 'VIP')
    const stdSeats = seatLayout.filter(seat => seat.seat_type === 'STD')
    
    return { VIP: vipSeats, STD: stdSeats }
}

/**
 * Prepare boat layout for VIP-only vessels
 */
export const prepareVIPOnlyBoatLayout = (availableSeats, assignedSeats) => {
    let seatLayout = []
    // Custom VIP layout doesn't use standard grid, will be handled separately
    // Create placeholder structure for compatibility
    availableSeats.forEach(seat => {
        const isBooked = seat.seat_status === 'BKD' || seat.seat_status === 'BOOKED'
        const isBlocked = seat.seat_status === 'BLK'
        const isAssigned = assignedSeats.includes(seat.seat_no)
        
        seatLayout.push({
            seat_no: seat.seat_no,
            seat_type: 'VIP',
            // Mark seat as unavailable if status is BLK (blocked), BKD (booked), BOOKED, or assigned
            // BKD/BOOKED seats should be marked as unavailable but still rendered
            available: !isBooked && !isBlocked && !isAssigned,
        })
    })
    
    return { VIP: seatLayout, STD: [] }
}
